import React, { useEffect, useMemo, useState } from 'react'
import { fmt } from '../api.js'
import { portal } from '../portalApi.js'
import DateRange from '../components/DateRange.jsx'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 자금 · 외화예금 — 통화별 통합 원장.
// 같은 USD면 어느 통장에 있든 단가가 같아야 하므로 계좌를 나누지 않는다. 계좌간 이체와
// RP 매수·매도는 내부 이동이라 원장에 안 나온다(단가를 건드리지 않는다).
const CURRENCIES = ['USD', 'CNY', 'AUD']
const ACQ = [
  { k: '환전', desc: '실제 환전 체결가를 넣습니다. 은행 환전거래내역에서 확인하세요.' },
  { k: '퇴결', desc: '나갈 때 단가를 그대로 복원합니다. 원 송금 건을 고르세요.' },
  { k: '이자', desc: '그날 고시환율로 잡습니다.' },
]

// 고시환율이 어디서 온 값인지 — 'smbs:CNH' 같은 코드를 사람이 읽는 문장으로
const RATE_SOURCE = { smbs: '서울외국환중개 매매기준율' }
// 원장은 거래 원본에서 계산하므로 승인 전에도 숫자가 나온다. 그 환차손익이 아직 확정
// 전표에 안 실렸다는 걸 구분해 보여준다.
const VOUCHER = { none: '미처리', requested: '요청중', approved: '승인', rejected: '반려' }
const rateNote = (l) => {
  if (!l.고시환율) return '고시환율 없음'
  const [site, code] = String(l.고시환율출처 || '').split(':')
  const parts = [RATE_SOURCE[site] || l.고시환율출처 || '출처 미상']
  if (code && code !== l.통화) parts.push(`${code} 대체 고시`)
  parts.push(`${l.고시환율적용일} 고시`)
  if (l.고시환율승계) parts.push(`거래일(${l.일자})은 고시 없음 — 직전 영업일 승계`)
  return parts.join(' · ')
}

// 단가가 왜 그 값인지 식으로 편다. 숫자는 전부 서버가 계산에 실제로 쓴 값이다 —
// 화면에서 다시 곱하면 반올림이 어긋나 근거가 오히려 의심을 만든다.
function Derivation({ line, currency }) {
  const g = line.산출근거
  if (!g) return null
  const n = (v, d = 0) => (v == null ? '—' : fmt(v, d, d))
  const rate = (v) => (v == null ? '—' : fmt(v, 6, 2))
  return (
    <tr className="fx-note">
      <td colSpan={14} className="l">
        {g.유형 === '입금' && (
          <div>
            <div className="fx-formula">
              (직전 장부원화 {n(g.직전장부원화)} + 취득 {n(g.취득원화)})
              {' ÷ '}
              (직전 잔액 {n(g.직전잔액, 2)} + 입금 {n(g.입금외화, 2)})
              {' = '}<b>{n(g.새단가표시, 2)}</b>
            </div>
            <div className="muted">
              취득 {n(g.취득원화)} = {n(g.입금외화, 2)} {currency} × {rate(g.취득환율)}
              {g.취득환율근거 && <> — {g.취득환율근거}</>}
            </div>
            <div className="muted">
              직전 단가 {rate(g.직전단가)} → 새 단가 {rate(g.새단가)}
              {' '}(표시는 소수 2자리 {n(g.직전단가표시, 2)} → {n(g.새단가표시, 2)})
            </div>
          </div>
        )}
        {g.유형 === '출금' && (
          <div>
            <div className="fx-formula">
              환차손익 <b>{n(g.환차손익)}</b> = 거래인식 {n(g.거래인식)} − 장부차감 {n(g.장부차감)}
            </div>
            <div className="muted">
              장부차감 {n(g.장부차감)} = {n(g.출금외화, 2)} {currency} × 총평균단가 {rate(g.직전단가)}
            </div>
            <div className="muted">
              거래인식 {n(g.거래인식)} = {g.확정인식원화
                ? <>실제 원화 출금액(은행 확정)</>
                : <>{n(g.출금외화, 2)} {currency} × 고시환율 {rate(g.고시환율)}</>}
            </div>
            <div className="muted">
              단가는 그대로입니다 — 잔액과 장부원화를 같은 단가로 덜어내기 때문입니다
              {g.새단가 != null && Math.abs(g.새단가 - g.직전단가) > 1e-9 &&
                <> (반올림으로 소수 이하만 {rate(g.직전단가)} → {rate(g.새단가)}로 미세하게 움직입니다)</>}
            </div>
          </div>
        )}
        {g.유형 === '미확정' && (
          <div className="muted">{g.설명}</div>
        )}
        {g.잔여털기 && (
          <div className="muted">
            잔액이 {n(g.잔여털기.잔액, 6)}까지 떨어져 반올림 잔여({n(g.잔여털기.장부원화)}원)를 털었습니다 —
            남기면 다음 단가가 크게 튑니다.
          </div>
        )}
      </td>
    </tr>
  )
}

const monthStart = (month) => `${month}-01`
const monthEnd = (month) => {
  const [y, m] = month.split('-').map(Number)
  return `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`
}

// 미확정 입금에 취득 유형을 지정하는 작은 팝업
function AcqModal({ line, sends, onClose, onSaved }) {
  const [type, setType] = useState(line.취득유형 || '환전')
  const [rate, setRate] = useState(line.취득환율 || '')
  const [origin, setOrigin] = useState('')
  const [memo, setMemo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const save = async () => {
    setBusy(true); setErr('')
    try {
      await portal.post('/api/fx/acquisition', {
        출처: line.출처, ticketId: line.ticket_id, 취득유형: type,
        취득환율: type === '환전' ? Number(rate) || null : null,
        원송금출처: 'bank', 원송금ticketId: type === '퇴결' ? origin || null : null,
        메모: memo || null,
      })
      onSaved(); onClose()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const ok = type === '이자' || (type === '환전' && Number(rate) > 0) || (type === '퇴결' && origin)

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel" onMouseDown={(e) => e.stopPropagation()} style={{ width: 560 }}>
        <div className="modal-header">
          <h3>취득 유형 지정</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ padding: 18 }}>
          <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
            {line.일자} · 입금 {fmt(line.입금외화, 2, 2)} · {line.적요 || line.상대}
          </div>
          <div className="seg" style={{ marginBottom: 10 }}>
            {ACQ.map((a) => (
              <label key={a.k} className={type === a.k ? 'on' : ''}>
                <input type="radio" name="acq" checked={type === a.k} onChange={() => setType(a.k)} />
                {a.k}
              </label>
            ))}
          </div>
          <div className="hint" style={{ marginBottom: 12 }}>{ACQ.find((a) => a.k === type)?.desc}</div>

          {type === '환전' && (
            <div className="row" style={{ marginBottom: 10 }}>
              <span className="muted" style={{ width: 90 }}>환전 체결가</span>
              <input type="number" step="0.01" value={rate} style={{ width: 140, textAlign: 'right' }}
                onChange={(e) => setRate(e.target.value)} />
              <span className="muted">원 / {line.통화 || ''}</span>
            </div>
          )}
          {type === '퇴결' && (
            <div className="row" style={{ marginBottom: 10 }}>
              <span className="muted" style={{ width: 90 }}>원 송금</span>
              <select value={origin} onChange={(e) => setOrigin(e.target.value)} style={{ maxWidth: 340 }}>
                <option value="">선택하세요</option>
                {sends.map((s) => (
                  <option key={s.ticket_id} value={s.ticket_id}>
                    {s.일자} · {fmt(s.출금외화, 2, 2)} · {s.적요}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="row">
            <span className="muted" style={{ width: 90 }}>메모</span>
            <input className="wide" style={{ width: 340 }} value={memo} onChange={(e) => setMemo(e.target.value)} />
          </div>
          {err && <div className="error-box" style={{ marginTop: 10 }}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn secondary" onClick={onClose}>취소</button>
          <button type="button" className="btn" disabled={!ok || busy} onClick={save}>
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function FxDeposit({ month, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [currency, setCurrency] = useState(() => {
    try { return localStorage.getItem('fx-currency') || 'USD' } catch { return 'USD' }
  })
  const [start, setStart] = useState(monthStart(month))
  const [end, setEnd] = useState(monthEnd(month))
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [acqFor, setAcqFor] = useState(null)

  useEffect(() => { setStart(monthStart(month)); setEnd(monthEnd(month)) }, [month])

  const load = () => {
    setErr(''); setLoading(true)
    portal.get(`/api/fx/ledger?currency=${currency}&start=${start}&end=${end}`)
      .then(setData)
      .catch((e) => setErr(e.status === 403 ? '외화예금 화면 권한이 없습니다.' : e.message))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [currency, start, end])

  const changeCurrency = (c) => {
    setCurrency(c)
    try { localStorage.setItem('fx-currency', c) } catch {}
  }

  // 퇴결로 지정할 때 고를 원 송금 목록
  const sends = useMemo(
    () => (data?.lines || []).filter((l) => (l.출금외화 || 0) > 0 && l.출처 === 'bank'),
    [data],
  )

  const [noteAt, setNoteAt] = useState(null)   // 산출근거를 편 줄
  const [dl, setDl] = useState(false)
  // 통화 탭을 바꾸면 새 원장이 오기 전까지 currency와 data가 어긋난다 — 라벨은 원장 쪽을 따른다
  const cur = data?.currency || currency
  const op = data?.opening
  const cl = data?.closing

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>외화예금</h2>
        <NoteDrawer target="fx-deposit" screenKey="treasury_fx"
          context={{ start, end, currency }} contextLabel={`${currency} ${start}~${end}`}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened} currentUserId={currentUserId} />
      </div>

      <div className="data-tabs">
        {CURRENCIES.map((c) => (
          <button key={c} type="button"
            className={'data-tab' + (currency === c ? ' active' : '')}
            onClick={() => changeCurrency(c)}>{c}</button>
        ))}
      </div>

      <div className="row" style={{ marginBottom: 10 }}>
        <DateRange start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e) }} />
        <button type="button" className="btn" disabled={dl || !data}
          title="계산 열을 수식으로 넣은 감사자료용 원장"
          onClick={async () => {
            setDl(true); setErr('')
            try {
              await portal.download(
                `/api/fx/ledger.xlsx?currency=${currency}&start=${start}&end=${end}`)
            } catch (e) { setErr(e.message) } finally { setDl(false) }
          }}>
          {dl ? '만드는 중…' : '엑셀 내려받기'}
        </button>
        {op && (
          <span className="muted">
            {op.구성 === '전기이월' ? '전기이월' : '기초'} {op.기준일} ·{' '}
            {fmt(op.외화잔액, 2, 2)} {cur} @{fmt(op.총평균단가, 2, 2)}
            {op.구성 && op.구성 !== '전기이월' && (
              <span className="muted"> ({op.구성})</span>
            )}
          </span>
        )}
        {data && data.미확정건수 > 0 && (
          <span style={{ color: 'var(--danger)' }}>단가 미확정 {data.미확정건수}건</span>
        )}
        {data && data.미승인건수 > 0 && (
          <span className="muted">전표 미승인 {data.미승인건수}건</span>
        )}
      </div>

      <div className="hint" style={{ marginBottom: 10 }}>
        같은 통화면 어느 계좌에 있든 단가가 같으므로 신한·한투를 한 원장으로 굴립니다.
        계좌간 이체와 RP 매수·매도는 내부 이동이라 여기 나오지 않습니다 — 외부와 오간 것만 단가에 영향을 줍니다.
        <br />
        <b>고시환율</b>은 <b>서울외국환중개</b>가 고시한 매매기준율입니다
        (<code>smbs.biz</code> 기간별 매매기준율, 매일 09:30 자동 수집).
        주말·공휴일은 고시가 없어 <b>직전 영업일</b> 값을 쓰고 그 날짜를 함께 적습니다.
        {currency === 'CNY' && ' 위안화는 중개사가 CNY 고시를 중단해 CNH(역외 위안)로 받습니다.'}
        {' '}환율 칸에 마우스를 올리면 건별 근거가 나옵니다.
      </div>
      {data && data.건전성 && !data.건전성.ok && (
        <div className="error-box" style={{ marginBottom: 10 }}>
          <b>이 원장은 아직 온전하지 않습니다 — 승인요청이 막혀 있습니다.</b>
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
            {data.건전성.문제.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
          <div className="muted" style={{ marginTop: 6 }}>
            총평균은 시간순 누적이라 유입 하나가 빠지면 그 뒤 단가가 전부 틀어집니다.
            자료가 채워지면 이 원장은 저절로 다시 맞지만, 승인 스냅샷은 그 순간 값을 박아
            틀린 채로 굳기 때문에 먼저 막습니다.
          </div>
        </div>
      )}
      {data && data.건전성 && data.건전성.ok && data.건전성.실제잔액 != null && (
        <div className="hint" style={{ marginBottom: 10 }}>
          <b>잔액 대사 확인</b> — 장부 {fmt(data.건전성.장부잔액, 2, 2)} {cur},
          실제 보유 {fmt(data.건전성.실제잔액, 2, 2)} {cur}
          (은행 {fmt(data.건전성.은행잔액, 2, 2)}
          {data.건전성.증권잔액 != null && <> + 한투 {fmt(data.건전성.증권잔액, 2, 2)}</>})로 일치합니다.
          {data.건전성.증권최종일 && <>{' '}한투 자료는 {data.건전성.증권최종일}까지 반영돼 있습니다 — 그 뒤로 거래가 없었다는 뜻입니다.</>}
        </div>
      )}
      {/* 숨기지는 않는다 — 작은 차이라도 숫자로 적어 두되, 까닭이 설명되면 전표를 막지 않는다 */}
      {data && data.건전성 && data.건전성.참고 && (
        <div className="hint" style={{ marginBottom: 10 }}>{data.건전성.참고}</div>
      )}
      {data && data.미승인건수 > 0 && (
        <div className="error-box" style={{ marginBottom: 10 }}>
          이 원장은 거래 원본에서 바로 계산합니다 — <b>전표 승인과 무관하게</b> 숫자가 나옵니다.
          지금 {data.미승인건수}건이 아직 승인 전이고, 승인된 건의 환차손익은{' '}
          <b>{fmt(data.확정환차손익)}원</b>입니다(합계 {fmt(data.환차손익합계)}원).
          <br />
          아직 계좌 화면 분개가 이 총평균단가를 쓰지 않아, 환차손익이 회계 장부로는 넘어가지 않은 상태입니다.
        </div>
      )}

      {err && <div className="error-box">{err}</div>}
      {!err && loading && <div className="muted">불러오는 중…</div>}
      {!err && !loading && data && (
        <div className="panel">
          <table>
            <thead>
              <tr>
                <th>일자</th><th>구분</th><th>전표</th><th className="l">적요</th>
                <th>입금({currency})</th><th>출금({currency})</th>
                <th>취득환율</th><th>고시환율</th>
                <th>장부차감</th><th>거래인식</th><th>환차손익</th>
                <th>잔액({currency})</th><th>장부원화</th><th>총평균단가</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.length === 0 && (
                <tr><td colSpan={14} className="c muted">해당 기간 외부 거래가 없습니다</td></tr>
              )}
              {data.lines.map((l, i) => ([
                <tr key={i} className={l.미확정 ? 'fx-unset' : undefined}>
                  <td className="c">{l.일자}</td>
                  <td className="c">{l.구분}</td>
                  <td className="c muted" style={{ fontSize: 11 }}>{VOUCHER[l.전표상태] || ''}</td>
                  <td className="l">{l.적요 || l.상대}</td>
                  <td>{l.입금외화 ? fmt(l.입금외화, 2, 2) : ''}</td>
                  <td>{l.출금외화 ? fmt(l.출금외화, 2, 2) : ''}</td>
                  <td>
                    {l.입금외화 ? (
                      l.적용취득환율 ? fmt(l.적용취득환율, 2, 2)
                        : <button type="button" className="link-cell" style={{ color: 'var(--danger)' }}
                            onClick={() => setAcqFor({ ...l, 통화: data.currency || currency })}>지정 필요</button>
                    ) : ''}
                  </td>
                  <td title={rateNote(l)}>
                    {l.고시환율 ? fmt(l.고시환율, 2, 2) : ''}
                    {l.고시환율승계 && (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {String(l.고시환율적용일 || '').slice(5)} 고시
                      </div>
                    )}
                  </td>
                  <td>{l.출금원화 ? fmt(l.출금원화) : ''}</td>
                  <td>{l.거래인식원화 ? fmt(l.거래인식원화) : ''}</td>
                  <td style={{ color: (l.환차손익 || 0) < 0 ? 'var(--danger)' : undefined }}>
                    {l.환차손익 ? fmt(l.환차손익) : ''}
                  </td>
                  <td>{fmt(l.잔액외화, 2, 2)}</td>
                  <td>{l.장부원화 != null ? fmt(l.장부원화) : '—'}</td>
                  <td>
                    {l.산출근거 ? (
                      <button type="button" className="link-cell"
                        title="총평균단가 산출근거 보기"
                        onClick={() => setNoteAt(noteAt === i ? null : i)}>
                        {l.총평균단가 != null ? fmt(l.총평균단가, 2, 2) : '—'}
                      </button>
                    ) : (l.총평균단가 != null ? fmt(l.총평균단가, 2, 2) : '—')}
                  </td>
                </tr>,
                noteAt === i && <Derivation key={`${i}-note`} line={l} currency={cur} />,
              ])).flat().filter(Boolean)}
              <tr className="total">
                <td colSpan={10} className="l">합계 · 기말</td>
                <td>{fmt(data.환차손익합계)}</td>
                <td>{cl ? fmt(cl.잔액외화, 2, 2) : ''}</td>
                <td>{cl && cl.장부원화 != null ? fmt(cl.장부원화) : '—'}</td>
                <td>{cl && cl.총평균단가 != null ? fmt(cl.총평균단가, 2, 2) : '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {acqFor && (
        <AcqModal line={acqFor} sends={sends} onClose={() => setAcqFor(null)} onSaved={load} />
      )}
    </>
  )
}
